<script setup lang="ts">
import AdsFilter from "@/components/ads/AdsFilter.vue"
import { ref, watch } from 'vue'
import { gql } from "@apollo/client/core"
import { useLazyQuery, useQuery, useResult } from '@vue/apollo-composable'
import { useAuthStore } from "@/stores/auth"
import { DateTime } from "luxon"
import InfiniteScroll from "../components/ui/InfiniteScroll.vue"
import FilterMenuDesktop from "../components/ads/FilterMenuDesktop.vue"
import AdCard from "../components/ads/AdCard.vue"
import FilterBarMobile from "../components/ads/FilterBarMobile.vue"
import CardTextIcon from "../components/icons/CardTextIcon.vue"
import TableIcon from "../components/icons/TableIcon.vue"
import AdTableView from '../components/ads/AdTableView.vue'
import DefaultLayout from '../components/layouts/DefaultLayout.vue'
import AlertSubscriptionExpiration from '../components/users/AlertSubscriptionExpiration.vue'

const authStore = useAuthStore()
const form = ref<Record<string, any>>({
    state: authStore.state,
    financed: false,
    // TODO: ativar por padrão daqui a um tempo
    // fromAuction: false
})
const filter = ref<Record<string, any>>({
    state: authStore.state,
    tags: []
})
const adsQueryEnabled = ref(false)

const { loading: staticResourcesLoading, error: staticResourcesError, result: staticResourcesResult, onResult: staticResourcesOnResult } = useQuery(gql`
  query StaticResources {
    states {
      name
      abbr
    }
    sources {
      name
      code
    }
    yearRanges: tagRangesByPrefix(prefix: "year-range") {
      name
      tag
    }
    priceRanges: tagRangesByPrefix(prefix: "price-range") {
      name
      tag
    }
    fipePercentRanges: tagRangesByPrefix(prefix: "fipe-percent-range") {
      name
      tag
    }
    fipeRanges: tagRangesByPrefix(prefix: "fipe-range") {
      name
      tag
    }
  }
`)
const states = useResult(staticResourcesResult, null, data => data.states)
const sources = useResult(staticResourcesResult, null, data => data.sources)
const yearRanges = useResult(staticResourcesResult, null, data => data.yearRanges)
const priceRanges = useResult(staticResourcesResult, null, data => data.priceRanges)
const fipePercentRanges = useResult(staticResourcesResult, null, data => data.fipePercentRanges)
const fipeRanges = useResult(staticResourcesResult, null, data => data.fipeRanges)

if (fipePercentRanges.value && fipePercentRanges.value.length) {
    form.value.fipePercentTag = fipePercentRanges.value[0].tag
    adsQueryEnabled.value = true
}

staticResourcesOnResult(() => {
    form.value.fipePercentTag = fipePercentRanges.value[0].tag
    adsQueryEnabled.value = true
})

const { load: loadCities, loading: citiesLoading, error: citiesError, result: citiesResult } = useLazyQuery(gql`
  query CitiesByStateAbbr($stateAbbr: String!) {
    citiesByStateAbbr(stateAbbr: $stateAbbr) {
      id
      name
      ref
    }
  }
`)
const cities = useResult(citiesResult, null, data => data.citiesByStateAbbr)

const { load: loadBrands, loading: brandsLoading, error: brandsError, result: brandsResult } = useLazyQuery(gql`
  query BrandsBySource($source: String!) {
    brandsBySource(source: $source) {
      id
      name
    }
  }
`)
const brands = useResult(brandsResult, null, data => data.brandsBySource)

const { load: loadModels, loading: modelsLoading, error: modelsError, result: modelsResult } = useLazyQuery(gql`
  query ModelsByBrandId($brandId: Key!) {
    modelsByBrandId(brandId: $brandId) {
      id
      name
    }
  }
`)
const models = useResult(modelsResult, null, data => data.modelsByBrandId)

const formChange = () => {
    const tags = []
    if (form.value.state !== filter.value.state) {
        delete form.value.city
    }
    if (form.value.state) {
        loadCities(undefined, {
            stateAbbr: form.value.state
        })
    }
    if (form.value.city) {
        tags.push(`city:${form.value.city}`)
    }
    const sourceTag = filter.value.tags.find((tag: string) => tag.startsWith("source:"))
    if (sourceTag && form.value.source !== sourceTag.split(":")[1]) {
        delete form.value.brandId
        delete form.value.modelId
    }
    if (form.value.source) {
        tags.push(`source:${form.value.source}`)
        loadBrands(undefined, {
            source: form.value.source
        })
        if (form.value.brandId) {
            tags.push(`brand:${form.value.brandId}`)
            loadModels(undefined, {
                brandId: form.value.brandId
            })
        }
        if (form.value.modelId) {
            tags.push(`model:${form.value.modelId}`)
        }
    }
    if (typeof form.value.isProfessional !== 'undefined') {
        tags.push(`is-professional:${form.value.isProfessional}`)
    }
    if (form.value.yearTag) {
        tags.push(form.value.yearTag)
    }
    if (form.value.priceTag) {
        tags.push(form.value.priceTag)
    }
    if (form.value.fipePercentTag) {
        tags.push(form.value.fipePercentTag)
    }
    if (form.value.fipeTag) {
        tags.push(form.value.fipeTag)
    }
    if (typeof form.value.financed !== 'undefined') {
        tags.push(`financed:${form.value.financed}`)
    }
    if (typeof form.value.fromAuction !== 'undefined') {
        tags.push(`from-auction:${form.value.fromAuction}`)
    }
    filter.value.state = form.value.state
    filter.value.tags = tags
}
formChange()
watch(form.value, formChange)

const { loading: adsLoading, error: adsError, result: adsResult, fetchMore: adsFetchMore } = useQuery(gql`
  query AdsByFilter($filter: AdsFilter!, $after: String) {
    adsByFilter (filter: $filter, first: 20, after: $after) {
      nodes {
        id
        link
        imagesUrl
        variantName
        brandName
        modelName
        state
        city
        price
        year
        isProfessional
        fipePercent
        fipePrice
        source
        createdAt
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`, { filter, after: <string | null>null }, () => ({ enabled: adsQueryEnabled.value }))
const ads = useResult(adsResult, null, data => {
    return data.adsByFilter.nodes
})

const adsLoadMore = () => {
    return adsFetchMore({
        variables: {
            after: adsResult.value.adsByFilter.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
            const newAds = fetchMoreResult.adsByFilter.nodes
            const pageInfo = fetchMoreResult.adsByFilter.pageInfo

            return newAds.length ? {
                ...previousResult,
                adsByFilter: {
                    ...previousResult.adsByFilter,
                    nodes: [
                        ...previousResult.adsByFilter.nodes,
                        ...newAds,
                    ],
                    pageInfo,
                }
            } : {
                ...previousResult,
                adsByFilter: {
                    ...previousResult.adsByFilter,
                    pageInfo,
                }
            }
        }
    })
}

const years = Array.from({ length: 20 + 2 }, (e, i) => DateTime.now().year + 1 - i)
const viewMode = ref('card')

const isUndefined = (value: any) => {
    return typeof value === 'undefined'
}
</script>

<template>
    <DefaultLayout>
        <div class="filter-bar-mobile">
            <FilterBarMobile :form="form" :states="states" :statesLoading="staticResourcesLoading" :cities="cities"
                :citiesLoading="citiesLoading" :sources="sources" :brands="brands" :brandsLoading="brandsLoading"
                :models="models" :modelsLoading="modelsLoading" :yearRanges="yearRanges" :years="years"
                :priceRanges="priceRanges" :fipePercentRanges="fipePercentRanges"
                :fipePercentRangesLoading="staticResourcesLoading" :fipeRanges="fipeRanges"
                :fipeRangesLoading="staticResourcesLoading" />
        </div>
        <article class="container framework-slot">
            <div class="row">
                <div class="col-4 col-lg-3 d-none d-md-block">
                    <div class="side-content">
                        <h5>Filtros avançados</h5>
                        <FilterMenuDesktop :form="form" :states="states" :statesLoading="staticResourcesLoading"
                            :cities="cities" :citiesLoading="citiesLoading" :sources="sources" :brands="brands"
                            :brandsLoading="brandsLoading" :models="models" :modelsLoading="modelsLoading"
                            :yearRanges="yearRanges" :years="years" :priceRanges="priceRanges"
                            :fipePercentRanges="fipePercentRanges" :fipePercentRangesLoading="staticResourcesLoading"
                            :fipeRanges="fipeRanges" :fipeRangesLoading="staticResourcesLoading" />
                    </div>
                </div>
                <div class="col-12 col-md-8 col-lg-9">
                    <div class="main-content">
                        <AlertSubscriptionExpiration />
                        <div class="ads-options-bar row">
                            <div class="filter-cloud col">
                                <AdsFilter v-if="states && form.state" filter-key="Estado"
                                    :filter-value="states.find((a: any) => form.state === a.abbr).name" mandatory="1" />
                                <AdsFilter v-if="cities && form.city" filter-key="Cidade"
                                    :filter-value="cities.find((a: any) => form.city === a.ref).name" />
                                <AdsFilter v-if="sources && form.source" filter-key="Plataforma"
                                    :filter-value="sources.find((a: any) => form.source === a.code).name"
                                    @remove="form.source = undefined" />
                                <AdsFilter v-if="brands && form.brandId" filter-key="Marca"
                                    :filter-value="brands.find((a: any) => form.brandId === a.id).name"
                                    @remove="form.brandId = undefined" />
                                <AdsFilter v-if="models && form.modelId" filter-key="Modelo"
                                    :filter-value="models.find((a: any) => form.modelId === a.id).name"
                                    @remove="form.modelId = undefined" />
                                <AdsFilter v-if="!isUndefined(form.isProfessional)" filter-key="Anunciante"
                                    :filter-value="form.isProfessional ? 'Pessoa Jurídica' : 'Pessoa Física'"
                                    @remove="form.isProfessional = undefined" />
                                <AdsFilter v-if="years && yearRanges && form.yearTag" filter-key="Ano"
                                    :filter-value="years.includes(parseInt(form.yearTag.replace('year:', ''), 10)) ? form.yearTag.replace('year:', '') : (yearRanges.find((a: any) => form.yearTag === a.tag).name)"
                                    @remove="form.yearTag = undefined" />
                                <AdsFilter v-if="priceRanges && form.priceTag" filter-key="Preço"
                                    :filter-value="priceRanges.find((a: any) => form.priceTag === a.tag).name"
                                    @remove="form.priceTag = undefined" />
                                <AdsFilter v-if="fipePercentRanges && form.fipePercentTag" filter-key="FIPE"
                                    :filter-value="fipePercentRanges.find((a: any) => form.fipePercentTag === a.tag).name"
                                    @remove="form.fipePercentTag = undefined" />
                                <AdsFilter v-if="fipeRanges && form.fipeTag" filter-key="FIPE-R$"
                                    :filter-value="fipeRanges.find((a: any) => form.fipeTag === a.tag).name"
                                    @remove="form.fipeTag = undefined" />
                                <AdsFilter v-if="!isUndefined(form.financed)" filter-key="Status financeiro"
                                    :filter-value="form.financed ? 'Alienado' : 'Quitado'"
                                    @remove="form.financed = undefined" />
                                <AdsFilter v-if="!isUndefined(form.fromAuction)" filter-key="De leilão"
                                    :filter-value="form.fromAuction ? 'Sim' : 'Não'"
                                    @remove="form.fromAuction = undefined" />
                            </div>
                            <div class="view-modes col-auto">
                                <button v-if="viewMode === 'table'" class="btn" type="button" @click="viewMode = 'card'">
                                    <TableIcon /><span>TABELA</span>
                                </button>
                                <button v-else class="btn" type="button" @click="viewMode = 'table'">
                                    <CardTextIcon /><span>CARD</span>
                                </button>
                            </div>
                        </div>
                        <span v-if="adsLoading">Carregando...</span>
                        <div v-else-if="adsError" class="alert alert-danger" role="alert">
                            {{ adsError.message }}
                        </div>
                        <InfiniteScroll v-else-if="ads" :handler="adsLoadMore"
                            :should-handle="adsResult.adsByFilter.pageInfo.hasNextPage" :manual="false">
                            <AdTableView v-if="viewMode === 'table'" :ads="ads" />
                            <ul v-if="viewMode === 'card'" class="ads card-view">
                                <AdCard v-for="ad of ads" :key="ad.id" :ad="ad" />
                            </ul>
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        </article>
    </DefaultLayout>
</template>

<style scoped>
.filter-bar-mobile {
    margin-top: calc(-1.5rem - 3px);
    margin-bottom: 1.5rem;
}

.side-content h5 {
    font-weight: 600;
    color: var(--white-ice);
    margin-bottom: 1rem;
}

.ads-options-bar {
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 0.5rem;
}

.view-modes .btn {
    font-size: 1rem;
    background-color: var(--black-eigengrau);
    border-radius: 0.25rem;
    padding: 5px 8px;
    margin-bottom: 0.5rem;
    color: var(--white-ice);
    box-shadow: none;
    text-align: left;
    display: flex;
    align-items: center;
    border: none;
}

.view-modes .btn svg {
    margin-right: 5px;
}

.view-modes .btn span {
    line-height: 1rem;
}

.side-content {
    background-color: var(--black-eigengrau);
    border-radius: 0.5rem;
    padding: 15px;
}

ul.ads {
    margin: 0 0 1.5rem;
    padding: 0;
}

.footer {
    margin: 0 auto;
}
</style>
